<template>
    <div class="relative group" :class="{ 'forms__field_input--wysiwyg--fullscreen': isFullscreen }">
        <div v-if="editor">
            <!-- Bubble: tools -->
            <bubble-menu
                class="flex overflow-hidden !bg-white rounded-md shadow dark:!bg-neutral-950 bubble-menu"
                :tippy-options="{ duration: 100 }"
                :editor="editor"
            >
                <template v-if="editor.isActive('image')">
                    <tip-tap-bar-section>
                        <tip-tap-button :active="imageHasClass('w-full')" @click="toggleFullWidth">
                            <icon icon="fa/solid/maximize" class="w-3 h-3"></icon>
                        </tip-tap-button>

                        <tip-tap-button :active="imageHasClass('float-left')" @click="setImageFloat('float-left')">
                            <icon icon="wysiwyg/float-left" class="w-3 h-3"></icon>
                        </tip-tap-button>

                        <tip-tap-button :active="imageHasClass('float-none')" @click="setImageFloat('float-none')">
                            <icon icon="wysiwyg/float-none" class="w-3 h-3"></icon>
                        </tip-tap-button>

                        <tip-tap-button :active="imageHasClass('float-right')" @click="setImageFloat('float-right')">
                            <icon icon="wysiwyg/float-right" class="w-3 h-3"></icon>
                        </tip-tap-button>
                    </tip-tap-bar-section>
                </template>
                <template v-else>
                    <tip-tap-bar-section>
                        <tip-tap-button
                            :active="editor.isActive('bold')"
                            @click="editor.chain().focus().toggleBold().run()"
                        >
                            <icon icon="fa/solid/bold" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :active="editor.isActive('italic')"
                            @click="editor.chain().focus().toggleItalic().run()"
                        >
                            <icon icon="fa/solid/italic" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :active="editor.isActive('underline')"
                            @click="editor.chain().focus().toggleUnderline().run()"
                        >
                            <icon icon="fa/solid/underline" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :active="editor.isActive('code')"
                            @click="editor.chain().focus().toggleCode().run()"
                        >
                            <icon icon="fa/solid/code" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button :active="editor.isActive('link')" @click="addLink">
                            <icon icon="fa/solid/link" class="w-3 h-3"></icon>
                        </tip-tap-button>
                    </tip-tap-bar-section>
                </template>
            </bubble-menu>

            <!-- Bubble: AI -->
            <bubble-menu
                v-if="props.field.options?.ai"
                class="flex overflow-hidden !bg-white rounded-md shadow dark:!bg-neutral-950 bubble-menu w-fit"
                :tippy-options="bubbleAiTippyOptions"
                :editor="editor"
            >
                <tip-tap-bar-section>
                    <tip-tap-button class="space-x-1 w-fit" @click="editor.chain().focus().aiComplete().run()">
                        <icon icon="ai/autocomplete" class="w-3 h-3"></icon>
                        <span class="text-xs">
                            {{ lang('fields::fields.wysiwyg.ai.autocomplete_short') }}
                        </span>
                    </tip-tap-button>
                    <tip-tap-button class="space-x-1 w-fit" @click="editor.chain().focus().aiGrammar().run()">
                        <icon icon="ai/grammar" class="w-3 h-3"></icon>
                        <span class="text-xs">
                            {{ lang('fields::fields.wysiwyg.ai.grammar_short') }}
                        </span>
                    </tip-tap-button>
                    <tip-tap-button class="space-x-1 w-fit" @click="editor.chain().focus().aiPrompt().run()">
                        <icon icon="ai/prompt" class="w-3 h-3"></icon>
                        <span class="text-xs">
                            {{ lang('fields::fields.wysiwyg.ai.prompt_short') }}
                        </span>
                    </tip-tap-button>
                    <tip-tap-dropdown>
                        <template #button>
                            <tip-tap-button class="space-x-1 w-fit">
                                <icon icon="ai/tone-of-voice" class="w-3 h-3"></icon>
                                <span class="text-xs">
                                    {{ lang('fields::fields.wysiwyg.ai.tone-of-voice_short') }}
                                </span>
                            </tip-tap-button>
                        </template>

                        <template #dropdown>
                            <ui-popover-section>
                                <ui-popover-segment>
                                    <tip-tap-button
                                        class="block w-full text-left"
                                        @click="editor.chain().focus().aiToneOfVoice('formal').run()"
                                    >
                                        {{
                                            lang('fields::fields.wysiwyg.ai.instructions.tone_of_voice.options.formal')
                                        }}
                                    </tip-tap-button>
                                    <tip-tap-button
                                        class="block w-full text-left"
                                        @click="editor.chain().focus().aiToneOfVoice('persuasive').run()"
                                    >
                                        {{
                                            lang(
                                                'fields::fields.wysiwyg.ai.instructions.tone_of_voice.options.persuasive',
                                            )
                                        }}
                                    </tip-tap-button>
                                    <tip-tap-button
                                        class="block w-full text-left"
                                        @click="editor.chain().focus().aiToneOfVoice('street').run()"
                                    >
                                        {{
                                            lang('fields::fields.wysiwyg.ai.instructions.tone_of_voice.options.street')
                                        }}
                                    </tip-tap-button>
                                </ui-popover-segment>
                            </ui-popover-section>
                        </template>
                    </tip-tap-dropdown>
                    <tip-tap-dropdown>
                        <template #button>
                            <tip-tap-button class="space-x-1 w-fit">
                                <icon icon="ai/translate" class="w-3 h-3"></icon>
                                <span class="text-xs">
                                    {{ lang('fields::fields.wysiwyg.ai.translate_short') }}
                                </span>
                            </tip-tap-button>
                        </template>

                        <template #dropdown>
                            <ui-popover-section>
                                <ui-popover-segment>
                                    <tip-tap-button
                                        class="block w-full text-left"
                                        @click="editor.chain().focus().aiTranslate('german').run()"
                                    >
                                        {{ lang('fields::fields.wysiwyg.ai.instructions.translate.options.german') }}
                                    </tip-tap-button>
                                    <tip-tap-button
                                        class="block w-full text-left"
                                        @click="editor.chain().focus().aiTranslate('english').run()"
                                    >
                                        {{ lang('fields::fields.wysiwyg.ai.instructions.translate.options.english') }}
                                    </tip-tap-button>
                                    <tip-tap-button
                                        class="block w-full text-left"
                                        @click="editor.chain().focus().aiTranslate('czech').run()"
                                    >
                                        {{ lang('fields::fields.wysiwyg.ai.instructions.translate.options.czech') }}
                                    </tip-tap-button>
                                </ui-popover-segment>
                            </ui-popover-section>
                        </template>
                    </tip-tap-dropdown>
                </tip-tap-bar-section>
            </bubble-menu>
        </div>
        <nav
            v-if="editor"
            ref="toolbarRef"
            class="forms__field_input--wysiwyg__toolbar"
            :class="[$props.showHideBar ? 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100' : '']"
        >
            <div class="forms__field_input--wysiwyg__toolbar__level">
                <div class="forms__field_input--wysiwyg__toolbar__section">
                    <!-- Text style -->
                    <tip-tap-bar-section>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.bold.tooltip')"
                            :active="editor.isActive('bold')"
                            @click="editor.chain().focus().toggleBold().run()"
                        >
                            <icon icon="fa/solid/bold" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.italic.tooltip')"
                            :active="editor.isActive('italic')"
                            @click="editor.chain().focus().toggleItalic().run()"
                        >
                            <icon icon="fa/solid/italic" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.underline.tooltip')"
                            :active="editor.isActive('underline')"
                            @click="editor.chain().focus().toggleUnderline().run()"
                        >
                            <icon icon="fa/solid/underline" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.code.tooltip')"
                            :active="editor.isActive('code')"
                            @click="editor.chain().focus().toggleCode().run()"
                        >
                            <icon icon="fa/solid/code" class="w-3 h-3"></icon>
                        </tip-tap-button>
                    </tip-tap-bar-section>

                    <tip-tap-bar-section>
                        <tip-tap-dropdown>
                            <template #button>
                                <tip-tap-button :tooltip="lang('fields::fields.wysiwyg.alignment.tooltip')">
                                    <icon
                                        v-if="editor.isActive({ textAlign: 'justify' })"
                                        icon="fa/solid/align-justify"
                                        class="w-3 h-3"
                                    ></icon>
                                    <icon
                                        v-else-if="editor.isActive({ textAlign: 'right' })"
                                        icon="fa/solid/align-right"
                                        class="w-3 h-3"
                                    ></icon>
                                    <icon
                                        v-else-if="editor.isActive({ textAlign: 'center' })"
                                        icon="fa/solid/align-center"
                                        class="w-3 h-3"
                                    ></icon>
                                    <icon
                                        v-else-if="editor.isActive({ textAlign: 'left' })"
                                        icon="fa/solid/align-left"
                                        class="w-3 h-3"
                                    ></icon>
                                    <Icon v-else icon="fa/solid/align-left" class="w-3 h-3"></Icon>
                                    <div class="absolute flex flex-col -translate-y-1/2 right-px top-1/2">
                                        <icon icon="fa/solid/caret-down" class="w-2 h-2"></icon>
                                    </div>
                                </tip-tap-button>
                            </template>
                            <template #dropdown>
                                <ui-popover-section>
                                    <ui-popover-segment>
                                        <tip-tap-bar-section>
                                            <tip-tap-button
                                                :tooltip="lang('fields::fields.wysiwyg.alignment.left')"
                                                @click="editor.commands.setTextAlign('left')"
                                            >
                                                <icon icon="fa/solid/align-left" class="w-3 h-3"></icon>
                                            </tip-tap-button>
                                            <tip-tap-button
                                                :tooltip="lang('fields::fields.wysiwyg.alignment.center')"
                                                @click="editor.commands.setTextAlign('center')"
                                            >
                                                <icon icon="fa/solid/align-center" class="w-3 h-3"></icon>
                                            </tip-tap-button>
                                            <tip-tap-button
                                                :tooltip="lang('fields::fields.wysiwyg.alignment.right')"
                                                @click="editor.commands.setTextAlign('right')"
                                            >
                                                <icon icon="fa/solid/align-right" class="w-3 h-3"></icon>
                                            </tip-tap-button>
                                            <tip-tap-button
                                                :tooltip="lang('fields::fields.wysiwyg.alignment.justify')"
                                                @click="editor.commands.setTextAlign('justify')"
                                            >
                                                <icon icon="fa/solid/align-justify" class="w-3 h-3"></icon>
                                            </tip-tap-button>
                                        </tip-tap-bar-section>
                                    </ui-popover-segment>
                                </ui-popover-section>
                            </template>
                        </tip-tap-dropdown>
                    </tip-tap-bar-section>

                    <tip-tap-bar-section>
                        <tip-tap-dropdown>
                            <template #button>
                                <tip-tap-button :tooltip="lang('fields::fields.wysiwyg.color.tooltip')">
                                    <icon
                                        icon="fa/solid/text"
                                        class="w-3 h-3"
                                        :style="{ color: editor.getAttributes('textStyle').color }"
                                    ></icon>
                                </tip-tap-button>
                            </template>
                            <template #dropdown>
                                <ui-popover-section>
                                    <ui-popover-segment>
                                        <div class="grid grid-cols-10 gap-1 p-1">
                                            <div
                                                v-for="color in palette"
                                                :key="color.hex"
                                                class="rounded-full cursor-pointer size-5 outline outline-offset-1 outline-2 hover:outline-neutral-500"
                                                :class="[
                                                    editor.getAttributes('textStyle').color === color.hex
                                                        ? 'outline-neutral-500'
                                                        : 'outline-transparent',
                                                ]"
                                                :style="{ backgroundColor: color.hex }"
                                                @click="editor.chain().focus().setColor(color.hex).run()"
                                            ></div>
                                        </div>
                                    </ui-popover-segment>
                                    <ui-popover-segment>
                                        <ui-popover-link @click="editor.chain().focus().unsetColor().run()">
                                            <icon icon="fa/solid/text-slash" class="w-3 h-3"></icon>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.color.unset') }}
                                            </span>
                                        </ui-popover-link>
                                    </ui-popover-segment>
                                </ui-popover-section>
                            </template>
                        </tip-tap-dropdown>
                    </tip-tap-bar-section>

                    <!-- Semantic style -->
                    <tip-tap-bar-section>
                        <tip-tap-dropdown>
                            <template #button>
                                <tip-tap-button
                                    class="!w-[100px] flex justify-between gap-1"
                                    :tooltip="lang('fields::fields.wysiwyg.text.tooltip')"
                                >
                                    <span v-if="editor.isActive('paragraph')" class="text-xs">
                                        {{ lang('fields::fields.wysiwyg.text.normal') }}
                                    </span>
                                    <span v-else-if="editor.isActive('heading', { level: 1 })" class="text-xs">
                                        {{ lang('fields::fields.wysiwyg.text.h1') }}
                                    </span>
                                    <span v-else-if="editor.isActive('heading', { level: 2 })" class="text-xs">
                                        {{ lang('fields::fields.wysiwyg.text.h2') }}
                                    </span>
                                    <span v-else-if="editor.isActive('heading', { level: 3 })" class="text-xs">
                                        {{ lang('fields::fields.wysiwyg.text.h3') }}
                                    </span>
                                    <span v-else-if="editor.isActive('heading', { level: 4 })" class="text-xs">
                                        {{ lang('fields::fields.wysiwyg.text.h4') }}
                                    </span>
                                    <span v-else-if="editor.isActive('heading', { level: 5 })" class="text-xs">
                                        {{ lang('fields::fields.wysiwyg.text.h5') }}
                                    </span>
                                    <span v-else-if="editor.isActive('heading', { level: 6 })" class="text-xs">
                                        {{ lang('fields::fields.wysiwyg.text.h6') }}
                                    </span>

                                    <icon icon="fa/solid/caret-down" class="w-2 h-2"></icon>
                                </tip-tap-button>
                            </template>
                            <template #dropdown>
                                <ui-popover-section>
                                    <ui-popover-segment>
                                        <ui-popover-link
                                            :active="editor.isActive('paragraph')"
                                            @click="editor.chain().focus().setParagraph().run()"
                                        >
                                            <icon icon="fa/solid/paragraph" class="w-3 h-3"></icon>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.text.normal') }}
                                            </span>
                                        </ui-popover-link>
                                        <ui-popover-link
                                            v-if="props.field.options?.text?.includes('h1')"
                                            :active="editor.isActive('heading', { level: 1 })"
                                            @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                                        >
                                            <span class="w-3 h-3">h1</span>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.text.h1') }}
                                            </span>
                                        </ui-popover-link>
                                        <ui-popover-link
                                            v-if="props.field.options?.text?.includes('h2')"
                                            :active="editor.isActive('heading', { level: 2 })"
                                            @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                                        >
                                            <span class="w-3 h-3">h2</span>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.text.h2') }}
                                            </span>
                                        </ui-popover-link>
                                        <ui-popover-link
                                            v-if="props.field.options?.text?.includes('h3')"
                                            :active="editor.isActive('heading', { level: 3 })"
                                            @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                                        >
                                            <span class="w-3 h-3">h3</span>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.text.h3') }}
                                            </span>
                                        </ui-popover-link>
                                        <ui-popover-link
                                            v-if="props.field.options?.text?.includes('h4')"
                                            :active="editor.isActive('heading', { level: 4 })"
                                            @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
                                        >
                                            <span class="w-3 h-3">h4</span>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.text.h4') }}
                                            </span>
                                        </ui-popover-link>
                                        <ui-popover-link
                                            v-if="props.field.options?.text?.includes('h5')"
                                            :active="editor.isActive('heading', { level: 5 })"
                                            @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
                                        >
                                            <span class="w-3 h-3">h5</span>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.text.h5') }}
                                            </span>
                                        </ui-popover-link>
                                        <ui-popover-link
                                            v-if="props.field.options?.text?.includes('h6')"
                                            :active="editor.isActive('heading', { level: 6 })"
                                            @click="editor.chain().focus().toggleHeading({ level: 6 }).run()"
                                        >
                                            <span class="w-3 h-3">h6</span>
                                            <span>
                                                {{ lang('fields::fields.wysiwyg.text.h6') }}
                                            </span>
                                        </ui-popover-link>
                                    </ui-popover-segment>
                                </ui-popover-section>
                            </template>
                        </tip-tap-dropdown>
                    </tip-tap-bar-section>

                    <!-- Font picker -->
                    <tip-tap-bar-section v-if="props.field?.options?.fonts">
                        <tip-tap-font-picker :editor="editor" />
                    </tip-tap-bar-section>

                    <!-- Font size picker -->
                    <tip-tap-bar-section v-if="props.field?.options?.font_sizes">
                        <tip-tap-font-size-picker :editor="editor" />
                    </tip-tap-bar-section>

                    <!-- Other text content -->
                    <tip-tap-bar-section>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.nonbreaking.tooltip')"
                            @click="editor.chain().focus().toggleNonBreakingSpace().run()"
                        >
                            <icon icon="fa/solid/minus" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.image.tooltip')"
                            :active="editor.isActive('image')"
                            @click="addImage"
                        >
                            <icon icon="fa/solid/image" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.bullet_list.tooltip')"
                            :active="editor.isActive('bulletList')"
                            @click="editor.chain().focus().toggleBulletList().run()"
                        >
                            <icon icon="fa/solid/list" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.bullet_list_sink.tooltip')"
                            :disabled="!editor.can().sinkListItem('listItem')"
                            @click="editor.chain().focus().sinkListItem('listItem').run()"
                        >
                            <div class="absolute left-0 flex flex-col -translate-y-1/2 top-1/2">
                                <icon icon="fa/solid/arrow-right" class="w-2 h-2"></icon>
                            </div>
                            <icon icon="fa/solid/list" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.bullet_list_lift.tooltip')"
                            :disabled="!editor.can().liftListItem('listItem')"
                            @click="editor.chain().focus().liftListItem('listItem').run()"
                        >
                            <div class="absolute left-0 flex flex-col -translate-y-1/2 top-1/2">
                                <icon icon="fa/solid/arrow-left" class="w-2 h-2"></icon>
                            </div>
                            <icon icon="fa/solid/list" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.ordered_list.tooltip')"
                            :active="editor.isActive('orderedList')"
                            @click="editor.chain().focus().toggleOrderedList().run()"
                        >
                            <icon icon="fa/solid/list-ol" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.link.tooltip')"
                            :active="editor.isActive('link')"
                            @click="addLink"
                        >
                            <icon icon="fa/solid/link" class="w-3 h-3"></icon>
                        </tip-tap-button>
                    </tip-tap-bar-section>

                    <!-- Special blocks -->
                    <tip-tap-bar-section>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.code_block.tooltip')"
                            :active="editor.isActive('codeBlock')"
                            @click="editor.chain().focus().toggleCodeBlock().run()"
                        >
                            <icon icon="fa/solid/square-code" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.blockquote.tooltip')"
                            :active="editor.isActive('blockquote')"
                            @click="editor.chain().focus().toggleBlockquote().run()"
                        >
                            <icon icon="fa/solid/quote-left" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.horizontal_rule.tooltip')"
                            @click="editor.chain().focus().setHorizontalRule().run()"
                        >
                            <icon icon="fa/solid/horizontal-rule" class="w-3 h-3"></icon>
                        </tip-tap-button>
                    </tip-tap-bar-section>

                    <!-- Table -->
                    <tip-tap-bar-section>
                        <tip-tap-dropdown>
                            <template #button>
                                <tip-tap-button :tooltip="lang('fields::fields.wysiwyg.table.create.tooltip')">
                                    <icon icon="fa/solid/table" class="w-3 h-3"></icon>
                                </tip-tap-button>
                            </template>
                            <template #dropdown="{ close }">
                                <tip-tap-table-grid :editor="editor" @close="close" />
                            </template>
                        </tip-tap-dropdown>
                    </tip-tap-bar-section>

                    <!-- User actions -->
                    <tip-tap-bar-section>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.undo.tooltip')"
                            @click="editor.chain().focus().undo().run()"
                        >
                            <icon icon="fa/solid/rotate-left" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :tooltip="lang('fields::fields.wysiwyg.redo.tooltip')"
                            @click="editor.chain().focus().redo().run()"
                        >
                            <icon icon="fa/solid/rotate-right" class="w-3 h-3"></icon>
                        </tip-tap-button>
                        <tip-tap-button
                            :active="isFullscreen"
                            :tooltip="isFullscreen ? lang('fields::fields.wysiwyg.exit_fullscreen.tooltip') : lang('fields::fields.wysiwyg.fullscreen.tooltip')"
                            @click="toggleFullscreen"
                        >
                            <icon :icon="isFullscreen ? 'fa/solid/compress' : 'fa/solid/expand'" class="w-3 h-3"></icon>
                        </tip-tap-button>
                    </tip-tap-bar-section>
                </div>
            </div>
        </nav>
        <div
            ref="editorWrapper"
            class="forms__field_input--wysiwyg__editor-wrapper"
            :class="[
                $props.showHideBar
                    ? 'forms__field_input--wysiwyg__editor-wrapper--show-hide-bar'
                    : 'forms__field_input--wysiwyg__editor-wrapper--show-bar',
            ]"
        >
            <div ref="editorTopRef" class="editor-top-observer"></div>

            <div class="forms__field_input--wysiwyg__editor-window wrapper-scrollable" @contextmenu="editorRightClick">
                <input
                    :id="props.id"
                    type="text"
                    class="sr-only"
                    :tabindex="props.tabindex"
                    @focusin="editor?.commands.focus()"
                />
                <editor-content :editor="editor" class="forms__field_input--wysiwyg__editor-inner" />
            </div>

            <tip-tap-context-menu
                ref="contextMenu"
                :editor="editor"
                :editor-wrapper="editorWrapper"
                @insert-image="addImage"
                @insert-file="addFile"
            ></tip-tap-context-menu>
        </div>

        <Modal :show="showLinkEditor" max-width="md" @close="closeLinkEditor">
            <div class="flex flex-col max-h-[80vh]">
                <UiModalHeader :title="lang('fields::fields.wysiwyg.link.title')" @close="closeLinkEditor" />

                <div class="flex-1 overflow-y-auto p-5 space-y-3">
                    <Field
                        v-model="editedLink.href"
                        label-position="left"
                        size="xs"
                        :field="{
                            adminComponent: 'AdminText',
                            name: lang('fields::fields.wysiwyg.link.href'),
                        }"
                    ></Field>
                    <div class="flex justify-end">
                        <Button color="default" size="xs" @click="openMediaGalleryForLink">
                            {{ lang('fields::fields.wysiwyg.link.select_from_media') }}
                        </Button>
                    </div>
                    <Field
                        v-model="editedLink.text"
                        label-position="left"
                        size="xs"
                        :field="{
                            adminComponent: 'AdminText',
                            name: lang('fields::fields.wysiwyg.link.text'),
                        }"
                    ></Field>
                    <Field
                        v-model="editedLink.target"
                        label-position="left"
                        size="xs"
                        :field="{
                            adminComponent: 'AdminSelect',
                            name: lang('fields::fields.wysiwyg.link.target'),
                        }"
                        :options="[
                            {
                                id: null,
                                name: lang('fields::fields.wysiwyg.link.targets._self'),
                            },
                            {
                                id: '_blank',
                                name: lang('fields::fields.wysiwyg.link.targets._blank'),
                            },
                        ]"
                    ></Field>
                    <!-- Rel -->
                    <Field
                        v-model="editedLink.noopener"
                        label-position="left"
                        size="xs"
                        :field="{
                            adminComponent: 'AdminCheckbox',
                            name: lang('fields::fields.wysiwyg.link.rel.noopener'),
                        }"
                    />
                    <Field
                        v-model="editedLink.noreferrer"
                        label-position="left"
                        size="xs"
                        :field="{
                            adminComponent: 'AdminCheckbox',
                            name: lang('fields::fields.wysiwyg.link.rel.noreferrer'),
                        }"
                    />
                    <Field
                        v-model="editedLink.nofollow"
                        label-position="left"
                        size="xs"
                        :field="{
                            adminComponent: 'AdminCheckbox',
                            name: lang('fields::fields.wysiwyg.link.rel.nofollow'),
                        }"
                    />
                </div>

                <div class="flex items-center justify-between gap-2 px-5 py-4 border-t border-neutral-200 dark:border-neutral-800">
                    <Button color="danger" @click="removeLink">{{ lang('general.remove') }}</Button>
                    <div class="flex items-center gap-2">
                        <Button color="default" @click="closeLinkEditor">{{ lang('general.close') }}</Button>
                        <Button color="primary" @click="applyLinkChanges">
                            {{ lang('general.save') }}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>

        <GalleryModal
            v-if="showMediaGallery"
            :multiple="selectMultipleInMediaGallery"
            @close="closeMediaGallery"
            @select="selectMediaFromGallery"
        />
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, computed, onMounted, onUnmounted } from 'vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/vue-3';
import { Color } from '@tiptap/extension-color';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Paragraph from '@tiptap/extension-paragraph';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Code from '@tiptap/extension-code';
import Underline from '@tiptap/extension-underline';
// import Image from '@tiptap/extension-image';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import History from '@tiptap/extension-history';
import Link from '@tiptap/extension-link';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Typography from '@tiptap/extension-typography';
import FileHandler from '@tiptap-pro/extension-file-handler';

import Heading from '@src/Fields/Pages/WYSIWYG/TipTap/Heading';
import Table from '@src/Fields/Pages/WYSIWYG/TipTap/Table';
import TableCell from '@src/Fields/Pages/WYSIWYG/TipTap/TableCell';
import TableRow from '@src/Fields/Pages/WYSIWYG/TipTap/TableRow';
import TableHeader from '@src/Fields/Pages/WYSIWYG/TipTap/TableHeader';
import TabToSpaces from '@src/Fields/Pages/WYSIWYG/TipTap/TabToSpaces';
import BulletList from '@src/Fields/Pages/WYSIWYG/TipTap/BulletList';
import ListItem from '@src/Fields/Pages/WYSIWYG/TipTap/ListItem';
import OrderedList from '@src/Fields/Pages/WYSIWYG/TipTap/OrderedList';

import { NonBreakingSpace } from '@src/Fields/Pages/WYSIWYG/Extensions/NonBreakingSpace';
import { CustomImage } from '@src/Fields/Pages/WYSIWYG/Extensions/CustomImage';
import { Ai } from '@src/Fields/Pages/WYSIWYG/Extensions/Ai';
import { FontSize } from '@src/Fields/Pages/WYSIWYG/Extensions/FontSize';

import TipTapButton from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapButton.vue';
import TipTapDropdown from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapDropdown.vue';
import TipTapBarSection from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapBarSection.vue';
import TipTapFontPicker from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapFontPicker.vue';
import TipTapFontSizePicker from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapFontSizePicker.vue';
import TipTapContextMenu from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapContextMenu.vue';
import TipTapTableGrid from '@src/Fields/Pages/WYSIWYG/TipTap/TipTapTableGrid.vue';

import GalleryModal from '@src/Media/Pages/GalleryModal.vue';
import { filesizeHumanReadable } from '@src/Common/Helpers/files';
import { extractFontComment, buildFontComment, injectFontStylesheets } from '@src/Fields/Pages/WYSIWYG/utils/wysiwygFonts';
import type { FontInfo } from '@src/Fields/Pages/WYSIWYG/utils/wysiwygFonts';
import Icon from '@src/Common/Components/Icon.vue';

const emit = defineEmits<{
    (event: 'update:modelValue', content: string): void;
}>();

const props = withDefaults(
    defineProps<
        | FieldComponent
        | {
              modelValue?: string | null;
              dusk?: string;
              showHideBar?: boolean;
              height?: string;
          }
    >(),
    {
        modelValue: '',
        dusk: 'tiptap-editor',
        showHideBar: false,
        height: 'h-40',
    },
);

const currentValue = ref(props.modelValue);
const cachedFontList = ref<FontInfo[]>([]);

// Strip font comment from content before passing to TipTap, inject stylesheets for preview
const { cleanHtml: initialCleanHtml, fonts: initialFonts } = extractFontComment(currentValue.value || '');
if (initialFonts.length) {
    injectFontStylesheets(initialFonts);
    currentValue.value = initialCleanHtml;
}

// Pre-fetch font list for buildFontComment
if (props.field?.options?.fonts) {
    fetch(route('fields.fonts.list'))
        .then((res) => res.json())
        .then((data: FontInfo[]) => {
            cachedFontList.value = data;
        })
        .catch(() => {});
}

const contextMenu = ref(null);
const editorWrapper = ref(null);
const isFullscreen = ref(false);

const toggleFullscreen = () => {
    isFullscreen.value = !isFullscreen.value;
};

/**
 * Upload image to server
 */
const uploadFile = (file) => {
    const data = new FormData();
    data.append('file', file);
    return axios.post(route('media.admin.api.create'), data).then(({ data }) => {
        return data.object;
    });
};

/**
 * TipTap editor
 */
const editor = useEditor({
    content: currentValue.value,
    editorProps: {
        attributes: {
            dusk: props.dusk,
            class: `prose prose-sm focus:outline-hidden dark:text-zinc-100 ${props.height}`,
        },
    },
    extensions: [
        Typography,
        Color,
        Blockquote,
        BulletList,
        ListItem,
        CodeBlock,
        Document,
        HardBreak,
        Heading,
        HorizontalRule,
        OrderedList,
        Paragraph,
        Bold,
        Italic,
        Code,
        Underline,
        Dropcursor,
        Gapcursor,
        History,
        CustomImage.configure({
            inline: true,
        }),
        Text,
        // Below Bold/Underline (100) on purpose, so the colour span is serialized
        // closest to the text (<strong><span style="color:…">) and its inline
        // colour wins over any rule the renderer puts on strong/u/headings.
        TextStyle.extend({ priority: 99 }),
        FontFamily,
        FontSize,
        TextAlign.configure({
            types: ['paragraph'],
            alignments: ['left', 'center', 'right', 'justify'],
        }),
        Link.configure({
            openOnClick: false,
            protocols: ['tel', 'mailto'],
            HTMLAttributes: {
                target: null,
            },
        }),
        Table.configure({
            resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        TabToSpaces,
        NonBreakingSpace,
        FileHandler.configure({
            allowedMimeTypes: [
                'image/png',
                'image/jpeg',
                'image/gif',
                'image/webp',

                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv',
                'image/svg+xml',
                'application/postscript',
                'application/zip',
                'application/x-rar-compressed',
            ],
            onDrop: async (currentEditor, files, pos) => {
                for (const file of files) {
                    const fileData = await uploadFile(file);

                    if (fileData.is_image) {
                        currentEditor
                            .chain()
                            .insertContentAt(pos, {
                                type: 'image',
                                attrs: {
                                    src: fileData.url,
                                },
                            })
                            .focus()
                            .run();
                    } else {
                        const linkText = `${fileData.name} (${filesizeHumanReadable(fileData.size)})`;

                        currentEditor.chain().insertContentAt(pos, linkText).focus().run();

                        const start = pos;
                        const end = pos + linkText.length;

                        currentEditor
                            .chain()
                            .setTextSelection({ from: start, to: end })
                            .setLink({ href: fileData.url, target: '_blank' })
                            .run();
                    }
                }
            },
            onPaste: async (currentEditor, files, htmlContent) => {
                for (const file of files) {
                    if (htmlContent) {
                        return false;
                    }

                    const fileData = await uploadFile(file);
                    const pos = currentEditor.state.selection.anchor;

                    if (fileData.is_image) {
                        currentEditor
                            .chain()
                            .insertContentAt(pos, {
                                type: 'image',
                                attrs: {
                                    src: fileData.url,
                                },
                            })
                            .focus()
                            .run();
                    } else {
                        const linkText = `${fileData.name} (${filesizeHumanReadable(fileData.size)})`;

                        currentEditor.chain().insertContentAt(pos, linkText).focus().run();

                        const start = pos;
                        const end = pos + linkText.length;

                        currentEditor
                            .chain()
                            .setTextSelection({ from: start, to: end })
                            .setLink({ href: fileData.url, target: '_blank' })
                            .run();
                    }
                }
            },
        }),
        Ai,
    ],

    onUpdate: () => {
        const html = editor.value.getHTML();
        const enriched = cachedFontList.value.length ? buildFontComment(html, cachedFontList.value) : html;
        emit('update:modelValue', enriched);
        currentValue.value = enriched;
    },
});


const editorRightClick = (event) => {
    event.preventDefault();

    contextMenu.value.toggleContextMenu();
};

/**
 * Link editor
 */
const showLinkEditor = ref(false);
const editedLink = ref({
    href: '',
    text: '',
    target: '',
    noopener: false,
    noreferrer: false,
    nofollow: false,
});

const closeLinkEditor = () => {
    showLinkEditor.value = false;
};

const openLinkEditor = () => {
    showLinkEditor.value = true;
};

const applyLinkChanges = () => {
    const relParts = [];
    if (editedLink.value.noopener) relParts.push('noopener');
    if (editedLink.value.noreferrer) relParts.push('noreferrer');
    if (editedLink.value.nofollow) relParts.push('nofollow');

    const finalRel = relParts.length ? relParts.join(' ') : null;

    editor.value
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({
            href: editedLink.value.href,
            target: editedLink.value.target,
            rel: finalRel,
        })
        .run();

    closeLinkEditor();
};

const removeLink = () => {
    editor.value.chain().focus().unsetLink().run();
    closeLinkEditor();
};

const addLink = () => {
    if (editor.value.isActive('link')) {
        editor.value.chain().focus().extendMarkRange('link').run();

        const { from, to } = editor.value.state.selection;
        const fullLinkText = editor.value.state.doc.textBetween(from, to, '\n');
        const linkAttrs = editor.value.getAttributes('link');

        const existingRel = linkAttrs.rel?.split(' ') ?? [];

        editedLink.value = {
            href: linkAttrs.href || '',
            target: linkAttrs.target || '',
            text: fullLinkText,
            noopener: existingRel.includes('noopener'),
            noreferrer: existingRel.includes('noreferrer'),
            nofollow: existingRel.includes('nofollow'),
        };
    } else {
        const { from, to } = editor.value.state.selection;
        const selectedText = editor.value.state.doc.textBetween(from, to, '\n');

        editedLink.value = {
            href: '',
            target: '',
            text: selectedText,
            noopener: false,
            noreferrer: false,
            nofollow: false,
        };
    }

    openLinkEditor();
};

/**
 * Media editor
 */
const showMediaGallery = ref(false);
const selectMultipleInMediaGallery = ref(false);
const mediaGalleryPurpose = ref<'image' | 'link' | 'file'>('image');

const closeMediaGallery = () => {
    showMediaGallery.value = false;
};

const openMediaGallery = () => {
    showMediaGallery.value = true;
};

const selectMediaFromGallery = (media) => {
    if (!media) {
        closeMediaGallery();
        return;
    }

    if (mediaGalleryPurpose.value === 'link') {
        editedLink.value.href = media.url;
        if (!editedLink.value.text) {
            editedLink.value.text = media.name;
        }
        closeMediaGallery();
        return;
    }

    if (mediaGalleryPurpose.value === 'file') {
        const pos = editor.value.state.selection.anchor;
        const linkText = `${media.name} (${filesizeHumanReadable(media.size)})`;

        editor.value.chain().insertContentAt(pos, linkText).focus().run();

        const start = pos;
        const end = pos + linkText.length;

        editor.value
            .chain()
            .setTextSelection({ from: start, to: end })
            .setLink({ href: media.url, target: '_blank' })
            .run();

        closeMediaGallery();
        return;
    }

    if (selectMultipleInMediaGallery.value) {
        media.forEach((item) => {
            editor.value.chain().focus().setImage({ src: item.url }).run();
        });
    } else {
        editor.value.chain().focus().setImage({ src: media.url }).run();
    }

    closeMediaGallery();
};

const addImage = () => {
    mediaGalleryPurpose.value = 'image';
    openMediaGallery();
};

const addFile = () => {
    selectMultipleInMediaGallery.value = false;
    mediaGalleryPurpose.value = 'file';
    openMediaGallery();
};

const openMediaGalleryForLink = () => {
    selectMultipleInMediaGallery.value = false;
    mediaGalleryPurpose.value = 'link';
    showMediaGallery.value = true;
};

/**
 * Image editor
 */
const imageHasClass = (className: string): boolean => {
    const attrs = editor.value.getAttributes('image');
    return attrs?.class?.split(' ').includes(className);
};

const updateImageClass = (newClasses: string[]) => {
    editor.value
        .chain()
        .focus()
        .updateAttributes('image', {
            class: newClasses.join(' '),
        })
        .run();
};

const toggleFullWidth = () => {
    const attrs = editor.value.getAttributes('image');
    const classes = attrs.class ? attrs.class.split(' ') : [];

    if (classes.includes('w-full')) {
        const filtered = classes.filter((c) => c !== 'w-full');
        updateImageClass(filtered);
    } else {
        classes.push('w-full');
        updateImageClass(classes);
    }
};

const setImageFloat = (floatClass: string) => {
    const attrs = editor.value.getAttributes('image');
    let classes = attrs.class ? attrs.class.split(' ') : [];

    classes = classes.filter((c) => !['float-left', 'float-none', 'float-start', 'float-right'].includes(c));

    if (floatClass !== 'float-none') {
        classes.push(floatClass);
    }

    updateImageClass(classes);
};

/**
 * Colors
 */
const palette = [
    // 1st row: Grey tones
    { hex: '#000000' },
    { hex: '#434343' },
    { hex: '#666666' },
    { hex: '#999999' },
    { hex: '#B7B7B7' },
    { hex: '#CCCCCC' },
    { hex: '#D9D9D9' },
    { hex: '#EFEFEF' },
    { hex: '#F3F3F3' },
    { hex: '#FFFFFF' },

    // 2nd row: Basic colors
    { hex: '#980000' },
    { hex: '#FF0000' },
    { hex: '#FF9900' },
    { hex: '#FFFF00' },
    { hex: '#00FF00' },
    { hex: '#00FFFF' },
    { hex: '#4A86E8' },
    { hex: '#0000FF' },
    { hex: '#9900FF' },
    { hex: '#FF00FF' },

    // 3rd row: Light colors
    { hex: '#EA9999' },
    { hex: '#F4CCCC' },
    { hex: '#FCE5CD' },
    { hex: '#FFF2CC' },
    { hex: '#D9EAD3' },
    { hex: '#D0E0E3' },
    { hex: '#C9DAF8' },
    { hex: '#CFE2F3' },
    { hex: '#D9D2E9' },
    { hex: '#EAD1DC' },

    // 4th row
    { hex: '#E06666' },
    { hex: '#EA9999' },
    { hex: '#F9CB9C' },
    { hex: '#FFE599' },
    { hex: '#B6D7A8' },
    { hex: '#A2C4C9' },
    { hex: '#A4C2F4' },
    { hex: '#9FC5E8' },
    { hex: '#B4A7D6' },
    { hex: '#D5A6BD' },

    // 5th row
    { hex: '#CC4125' },
    { hex: '#E06666' },
    { hex: '#F6B26B' },
    { hex: '#FFD966' },
    { hex: '#93C47D' },
    { hex: '#76A5AF' },
    { hex: '#6D9EEB' },
    { hex: '#6FA8DC' },
    { hex: '#8E7CC3' },
    { hex: '#C27BA0' },

    // 6th row
    { hex: '#A61C00' },
    { hex: '#CC0000' },
    { hex: '#E69138' },
    { hex: '#F1C232' },
    { hex: '#6AA84F' },
    { hex: '#45818E' },
    { hex: '#3C78D8' },
    { hex: '#3D85C6' },
    { hex: '#674EA7' },
    { hex: '#A64D79' },

    // 7th row: Dark tones
    { hex: '#741B47' },
    { hex: '#660000' },
    { hex: '#783F04' },
    { hex: '#7F6000' },
    { hex: '#274E13' },
    { hex: '#0C343D' },
    { hex: '#1C4587' },
    { hex: '#073763' },
    { hex: '#20124D' },
    { hex: '#4C1130' },
];

/**
 * General actions
 */
onBeforeUnmount(() => {
    editor.value.destroy();
});

/**
 * Bubble menus
 */
const bubbleAiTippyOptions = computed(() => {
    if (typeof window !== 'undefined') {
        return {
            duration: 100,
            placement: 'bottom',
            appendTo: document.body,
            interactive: true,
            hideOnClick: false,
        };
    } else {
        return {
            duration: 100,
            placement: 'bottom',
        };
    }
});

/**
 * Stay on top toolbar
 */
const toolbarRef = ref(null);
const editorTopRef = ref(null);
const isFixed = ref(false);

const updateToolbarSize = () => {
    if (!toolbarRef.value || !editorTopRef.value) return;

    const editorRect = editorTopRef.value.getBoundingClientRect();

    toolbarRef.value.style.width = `${editorRect.width}px`;
    toolbarRef.value.style.left = `${editorRect.left}px`;
};

const handleScroll = () => {
    if (!toolbarRef.value || !editorTopRef.value) return;

    const editorTopRect = editorTopRef.value.getBoundingClientRect();

    const editorBottomRect = editorWrapper.value.getBoundingClientRect();

    if (editorTopRect.top < 0 && editorBottomRect.bottom > 0) {
        toolbarRef.value.classList.add('forms__field_input--wysiwyg__toolbar--fixed');
        toolbarRef.value.classList.remove('forms__field_input--wysiwyg__toolbar--sticky');

        updateToolbarSize();
        window.addEventListener('resize', updateToolbarSize);
        isFixed.value = true;
    } else {
        toolbarRef.value.classList.remove('forms__field_input--wysiwyg__toolbar--fixed');
        toolbarRef.value.classList.add('forms__field_input--wysiwyg__toolbar--sticky');
        toolbarRef.value.style.width = '';

        window.removeEventListener('resize', updateToolbarSize);
        isFixed.value = false;
    }
};

const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isFullscreen.value) {
        isFullscreen.value = false;
    }
};

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleEscape);

    const main = document.querySelector('#main');

    if (main) {
        main.addEventListener('scroll', handleScroll);
    }
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', updateToolbarSize);
    window.removeEventListener('keydown', handleEscape);

    const main = document.querySelector('#main');

    if (main) {
        main.removeEventListener('scroll', handleScroll);
    }
});
</script>

<style>
/* Basic editor styles */
.ProseMirror {
    width: calc(100% - 2.5rem);
    max-width: none;
}

.ProseMirror > * + * {
    margin-top: 0.75em;
}

.ProseMirror ul,
.ProseMirror ol {
    padding: 0 1rem;
}

.ProseMirror h1,
.ProseMirror h2,
.ProseMirror h3,
.ProseMirror h4,
.ProseMirror h5,
.ProseMirror h6 {
    line-height: 1.1;
}

/* Headings, strong and links inherit from .dark .ProseMirror below. Setting
   color here would outrank the author's inline colour on an enclosing span. */

.ProseMirror code {
    background-color: rgba(#616161, 0.1);
    color: #616161;
}

.ProseMirror pre {
    background: #0d0d0d;
    color: #fff;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
}

.ProseMirror pre code {
    color: inherit;
    padding: 0;
    background: none;
    font-size: 0.8rem;
}

.ProseMirror img {
    max-width: 100%;
    height: auto;
}

.ProseMirror img.ProseMirror-selectednode {
    outline: 3px solid #ac0ca7;
}

.ProseMirror blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0d0d0d, 0.1);
}

.ProseMirror hr {
    border: none;
    border-top: 2px solid rgba(#0d0d0d, 0.1);
    margin: 2rem 0;
}

.dark .ProseMirror {
    color: #dcdcdc;
}

.dark .ProseMirror a:not([style*='color'] *) {
    color: #fff;
}

.wrapper-scrollable::-webkit-scrollbar {
    width: 20px;
}

.wrapper-scrollable::-webkit-scrollbar-track {
    background-color: transparent;
}

.wrapper-scrollable::-webkit-scrollbar-thumb {
    background-color: #d6dee1;
    border-radius: 20px;
    border: 6px solid transparent;
    background-clip: content-box;
}

.wrapper-scrollable::-webkit-scrollbar-thumb:hover {
    background-color: #a8bbbf;
}

.tiptap table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;
}

.tiptap table td,
.tiptap table th {
    min-width: 1em;
    border: 2px solid #ced4da;
    padding: 3px 5px;
    vertical-align: top;
    box-sizing: border-box;
    position: relative;
}

.dark .tiptap table td,
.dark .tiptap table th {
    border-color: #3d4852;
}

.tiptap table td > *,
.tiptap table th > * {
    margin-bottom: 0;
}

.tiptap table th {
    font-weight: bold;
    text-align: left;
    background-color: #f1f3f5;
}

.dark .tiptap table th {
    background-color: #2d3748;
}

.tiptap .selectedCell:after {
    z-index: 2;
    position: absolute;
    content: '';
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(200, 200, 255, 0.4);
    pointer-events: none;
}

.tiptap .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: -2px;
    width: 4px;
    background-color: #adf;
    pointer-events: none;
}
</style>
